// REACT
import React, { useState } from "react";

// MATERIAL-UI
import {
  Grid,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Typography,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";

// COMPONENTS
import QrModal from "../Modal/QR/QrModal";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import useTableSort from "src/utils/hooks/useTableSort";

// ASSETS

import { HoldingsListStyles } from "../common/styles/HoldingsList.styles";
import formatCoinName from "src/utils/formatCoinName";
import CopyAddress from "../Modal/QR/CopyAddress";
import GA from "src/analytics/googleAnalytics";
import TableSort from "src/components/common/TableSort";

// TYPES
import { HeadCell, Order } from "src/components/common/tableTypes";
import MemoModal from "../Modal/memo/MemoModal";
import { RowDataType } from "src/pages/Addresses";
import AddressActionButton from "./AddressActionButton";
import WalletNicknameEditor from "./WalletNicknameEditor";
import api from "src/store/features/api";

// Type definitions moved to the top for better organization

export type TokenData = {
  code: string;
  name: string;
  address: string;
  logoUrl?: string;
  memo?: string;
  format: string;
  depositable: boolean;
};

export type AddressTableProps = {
  data: RowDataType[];
  handleRowClick: (el: RowDataType) => void;
  initialSort?: {
    orderBy: string;
    order: Order;
  };
};

export type CurrencyMemoModal = {
  name: string;
  icon?: string;
};

// Constants moved outside component to prevent recreation on render
const DEFAULT_TOKEN: TokenData = {
  code: "",
  name: "",
  address: "",
  logoUrl: "",
  memo: "",
  format: "",
  depositable: true,
};

const HEAD_CELLS: HeadCell[] = [
  { id: "code", label: "Coin" },
  { id: "format", label: "Format" },
  { id: "address", label: "Address" },
  { id: "nickname", label: "Nickname" },
  { id: "status", label: "Status" },
  { id: "memo", label: "Memo" },
  { id: "copy", label: "" },
];

// Helper functions moved outside component to prevent recreation on render
const formatData = (input: string): string => {
  const maxLength = 10;
  return input.length > maxLength ? `${input.substring(0, maxLength)}...` : input;
};

const formatLongMemos = (name: string): string => {
  const max = 20;
  return name.length > max ? `${name.slice(0, max)}...` : name;
};

function shortenAddress(address: string, startChars = 6, endChars = 4) {
  // Handle edge cases
  if (!address) return "";

  // If the address is already shorter than or equal to startChars + endChars, return it as is
  if (address.length <= startChars + endChars) {
    return address;
  }

  // Take the first startChars and last endChars, connecting them with an ellipsis
  const start = address.substring(0, startChars);
  const end = address.substring(address.length - endChars);

  return `${start}...${end}`;
}

export const AddressTable: React.FC<AddressTableProps> = ({
  data,
  handleRowClick,
  initialSort = { orderBy: "code", order: "asc" },
}): JSX.Element => {
  const classes = HoldingsListStyles();

  // State management with proper typing
  const [qrModalOpen, setQrModalOpen] = useState<boolean>(false);
  const [copyModalOpen, setCopyModalOpen] = useState<boolean>(false);
  const [token, setToken] = useState<TokenData>(DEFAULT_TOKEN);
  const [memo, setMemo] = useState<string | undefined>("");
  const [currency, setCurrency] = useState<CurrencyMemoModal | undefined>(undefined);
  const [memoModalOpen, setMemoModalOpen] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>(initialSort.order);
  const [orderBy, setOrderBy] = useState<string>(initialSort.orderBy);

  const [updateAddress] = api.useUpdateUserWithdrawAddressMutation();

  // Import table sorting hook
  const [sort, compare] = useTableSort();

  // Event handlers with proper typing using useCallback to prevent unnecessary rerenders
  const handleQrModalClose = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>) => {
      event.stopPropagation();
      setQrModalOpen(false);
      setToken(DEFAULT_TOKEN);
      GA.modal("QR", "close");
    },
    [],
  );

  const handleCopyModalClose = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>) => {
      event.stopPropagation();
      setCopyModalOpen(false);
    },
    [],
  );

  const handleMemoModalClose = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>) => {
      event.stopPropagation();
      setMemoModalOpen(false);
      GA.modal("QR", "close");
    },
    [],
  );

  const handleMemoClick = React.useCallback(
    (e: React.MouseEvent, memo?: string, currencyConfig?: any) => {
      e.stopPropagation();
      if (memo) {
        setMemo(memo);
        setCurrency(
          currencyConfig
            ? {
                name: currencyConfig.formatName,
                icon: currencyConfig.logoUrl,
              }
            : undefined,
        );
        setMemoModalOpen(true);
      }
    },
    [],
  );

  const handleRequestSort = React.useCallback(
    (property: string) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy],
  );

  // Use useMemo for expensive computations
  const sortedData = React.useMemo((): RowDataType[] => {
    return sort(data, compare(order, orderBy));
  }, [data, sort, compare, order, orderBy]);

  return (
    <>
      <Table className={classes.table} aria-label="cryptocurrency address table">
        <TableHead>
          <TableRow
            style={{
              borderBottom: "3px solid #313F4F",
            }}>
            {HEAD_CELLS.map((headCell) => (
              <TableCell key={headCell.id} className={classes.tableHeadItem}>
                <TableSort
                  order={order}
                  orderBy={orderBy}
                  onClick={handleRequestSort}
                  headCell={headCell}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row, index) => (
            <TableRow
              key={`${row.code}-${row.address}-${index}`}
              hover
              className={classes.tableRow}
              onClick={() => handleRowClick(row)}
              aria-label={`Row for ${row.code}`}>
              <TableCell>
                <Grid container alignItems="center" direction="row" wrap="nowrap">
                  <CountryOrCryptoFlag name={row.logoUrl || row.code} size={40} />
                  <ListItem sx={{ height: "20px" }}>
                    <ListItemText
                      primary={<b>{row.code}</b>}
                      secondary={formatCoinName(row.name)}
                    />
                  </ListItem>
                </Grid>
              </TableCell>
              <TableCell>{formatData(row.format)}</TableCell>
              <TableCell
                sx={{
                  overflowX: "scroll",
                  "&::-webkit-scrollbar": { display: "none" },
                  "&::WebkitMsOverflowStyle": "none",
                }}
                title={row.address} // For accessibility
              >
                {shortenAddress(row.address, 8, 8)}
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <WalletNicknameEditor
                    initialNickname={row.nickname || `My ${row.code} Wallet`}
                    walletAddress={row.address}
                    format={row.format}
                    status={row.status}
                    memo={Boolean(row.memo)}
                    currencyCode={row.currencyConfig.code}
                    onSave={async (
                      id,
                      format,
                      status,
                      memo,
                      currencyCode,
                      newName,
                    ): Promise<void> => {
                      // Your save logic here
                      await updateAddress({
                        updateAddressDto: {
                          address: id,
                          format,
                          status,
                          memo,
                          currencyCode,
                          nickname: newName,
                        },
                      });
                    }}
                  />
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: "14px" }}>{row.status}</Typography>
                </Stack>
              </TableCell>
              <TableCell align="left" sx={!row.memo ? { paddingLeft: "30px" } : undefined}>
                {row.memo ? (
                  <Typography
                    onClick={(e) => handleMemoClick(e, row.memo, row.currencyConfig)}
                    sx={{ cursor: "pointer" }}
                    aria-label={`View memo for ${row.code}`}>
                    {formatLongMemos(row.memo)}
                  </Typography>
                ) : (
                  <Typography>-</Typography>
                )}
              </TableCell>
              <TableCell>
                <AddressActionButton
                  el={row}
                  handleRowClick={handleRowClick}
                  setCopy={setCopyModalOpen}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Modals */}
      {copyModalOpen && <CopyAddress open={copyModalOpen} onClose={handleCopyModalClose} />}
      <QrModal token={token} open={qrModalOpen} onClose={handleQrModalClose} />
      <MemoModal
        memo={memo}
        currency={currency}
        open={memoModalOpen}
        onClose={handleMemoModalClose}
      />
    </>
  );
};

export default AddressTable;
